import {createSlice, createSelector, PayloadAction} from '@reduxjs/toolkit';
import type {ThunkAction, Action} from '@reduxjs/toolkit';
import {userApi} from '@api/user';
import {accountUserApi} from '@api/accountUser/api';
import type {AccountUserResource, AccountUserStatus} from '@api/accountUser/api.d';
import type {User, PaginatedResponse, UsersListParams} from '@api/user/api.d';
import {getErrorMessage} from '../../utils/requests/error';
import {getActiveAccountId, getActiveUserIdsSet} from '@utils/account';
import {getResponseBody} from '@utils/apiResponse';
import type {ErrorMessage} from '@utils/requests/error.types';

export interface SenderRow {
    accountUser: AccountUserResource;
    user: User;
}

export interface SendersOptions {
    query: string;
    sort: string;
    dir: string;
    from: number;
    to: number;
    search: string;
}

export interface SendersState {
    accountUsers: AccountUserResource[];
    allAccountUsers: AccountUserResource[];
    users: Record<string, User>;
    sendersPerPage: number;
    sendersOptions: SendersOptions;
    sendersCount: number | null;
    allSendersCount: number | null;
    isFetchingSenders: boolean;
    sendersFetchingError: ErrorMessage | null;
    inviteSenderError: ErrorMessage | null;
    updatingSenderError: ErrorMessage | null;
    isUpdatingSender: boolean;
    deletingSenderError: ErrorMessage | null;
    isDeletingSender: boolean;
}

export const DEFAULT_SENDERS_OPTIONS: SendersOptions = {
    query: 'accountSenders',
    sort: 'name',
    dir: 'asc',
    from: 1,
    to: 15,
    search: ''
};

const initialState: SendersState = {
    accountUsers: [],
    allAccountUsers: [],
    users: {},
    sendersPerPage: 15,
    sendersOptions: DEFAULT_SENDERS_OPTIONS,
    sendersCount: null,
    allSendersCount: null,
    isFetchingSenders: false,
    sendersFetchingError: null,
    inviteSenderError: null,
    updatingSenderError: null,
    isUpdatingSender: false,
    deletingSenderError: null,
    isDeletingSender: false
};

export const sendersSlice = createSlice({
    name: 'senders',
    initialState,
    reducers: {
        setSenders: (state, action: PayloadAction<{accountUsers: AccountUserResource[]; users: User[]}>) => {
            state.accountUsers = action.payload.accountUsers;
            action.payload.users.forEach((user) => {
                state.users[user.id] = user;
            });
        },
        setAllSenders: (state, action: PayloadAction<{accountUsers: AccountUserResource[]; users: User[]}>) => {
            state.allAccountUsers = action.payload.accountUsers;
            action.payload.users.forEach((user) => {
                state.users[user.id] = user;
            });
        },
        setIsFetchingSenders: (state, action: PayloadAction<boolean>) => {
            state.isFetchingSenders = action.payload;
        },
        setSendersFetchingError: (state, action: PayloadAction<ErrorMessage | null>) => {
            state.sendersFetchingError = action.payload;
        },
        setSendersOptions: (state, action: PayloadAction<SendersOptions>) => {
            state.sendersOptions = action.payload;
        },
        setSendersCount: (state, action: PayloadAction<number | null>) => {
            state.sendersCount = action.payload;
        },
        setAllSendersCount: (state, action: PayloadAction<number | null>) => {
            state.allSendersCount = action.payload;
        },
        setInviteSenderError: (state, action: PayloadAction<ErrorMessage | null>) => {
            state.inviteSenderError = action.payload;
        },
        setUpdatingSenderError: (state, action: PayloadAction<ErrorMessage | null>) => {
            state.updatingSenderError = action.payload;
        },
        setIsUpdatingSender: (state, action: PayloadAction<boolean>) => {
            state.isUpdatingSender = action.payload;
        },
        setDeletingSenderError: (state, action: PayloadAction<ErrorMessage | null>) => {
            state.deletingSenderError = action.payload;
        },
        setIsDeletingSender: (state, action: PayloadAction<boolean>) => {
            state.isDeletingSender = action.payload;
        }
    }
});

export const {
    setSenders,
    setAllSenders,
    setIsFetchingSenders,
    setSendersFetchingError,
    setSendersOptions,
    setSendersCount,
    setAllSendersCount,
    setInviteSenderError,
    setUpdatingSenderError,
    setIsUpdatingSender,
    setDeletingSenderError,
    setIsDeletingSender
} = sendersSlice.actions;

interface RootStateShape {
    senders: SendersState;
    user: {
        activeAccountId: string | null;
        account?: {id?: string} | null;
    };
}

type SendersThunk<ReturnType> = ThunkAction<
    Promise<ReturnType>,
    RootStateShape,
    unknown,
    Action<string>
>;

export interface SenderProfileInput {
    id: string;
    firstName?: string;
    lastName?: string;
    company?: string;
    title?: string;
    status?: AccountUserStatus;
}

export interface SenderInviteInput {
    email: string;
    firstName?: string;
    lastName?: string;
    company?: string;
    title?: string;
}

const getAccountId = (state: RootStateShape): string => getActiveAccountId(state.user);

const getUsersForAccountUsers = async (accountUsers: AccountUserResource[]): Promise<User[]> => {
    const userResponses = await Promise.all(
        accountUsers.map((accountUser) => userApi.getUser({userId: accountUser.user_id}))
    );
    return userResponses.map((response) => response.data);
};

const getPaginatedAccountUsers = async (
    accountId: string,
    params: {page?: number; per_page?: number; scope?: 'all' | 'active' | 'inactive' | 'pending'}
): Promise<PaginatedResponse<AccountUserResource>> => {
    const response = await accountUserApi.getAccountUsers({accountId, params});
    return response as unknown as PaginatedResponse<AccountUserResource>;
};

const getSeatConsumingAccountUsersCount = async (accountId: string): Promise<number> => {
    const [allAccountUsers, lockedAccountUsers] = await Promise.all([
        getPaginatedAccountUsers(accountId, {per_page: 1, scope: 'all'}),
        getPaginatedAccountUsers(accountId, {per_page: 1, scope: 'inactive'})
    ]);
    return allAccountUsers.meta.total - lockedAccountUsers.meta.total;
};

export const searchSender = (search: string): SendersThunk<{accountUsers: AccountUserResource[]; users: User[]}> => async (dispatch, getState) => {
    const accountId = getAccountId(getState());

    const [activeUserIds, usersResponse] = await Promise.all([
        getActiveUserIdsSet(accountId),
        userApi.getUsers({params: {search, page: 1, per_page: 100} as UsersListParams})
    ]);
    const users = (usersResponse as unknown as PaginatedResponse<User>).data;
    const matchedUsers = users.filter((user) => activeUserIds.has(user.id));
    const accountUsers = await Promise.all(
        matchedUsers.map(async (user) => {
            const response = await accountUserApi.getAccountUserByAccountAndUser({accountId, userId: user.id});
            return getResponseBody<AccountUserResource>(response);
        })
    );
    dispatch(setAllSendersCount(matchedUsers.length));
    return {accountUsers, users: matchedUsers};
};

export const fetchSenders = (): SendersThunk<boolean> => async (dispatch, getState) => {
    const state = getState();
    const accountId = getAccountId(state);
    const {from, to, search} = state.senders.sendersOptions;
    const perPage = to ? (to - from) + 1 : 15;
    const page = Math.ceil(from / perPage);

    dispatch(setIsFetchingSenders(true));
    dispatch(setSendersFetchingError(null));

    try {
        if (search) {
            const {accountUsers, users} = await dispatch(searchSender(search));
            dispatch(setSenders({accountUsers, users}));
            dispatch(setSendersCount(accountUsers.length));
            dispatch(setIsFetchingSenders(false));
            return true;
        }

        const response = await getPaginatedAccountUsers(accountId, {page, per_page: perPage, scope: 'all'});
        const accountUsers = response.data;
        const users = await getUsersForAccountUsers(accountUsers);

        dispatch(setSenders({accountUsers, users}));
        dispatch(setSendersCount(response.meta.total));
        dispatch(setIsFetchingSenders(false));
        return true;
    } catch (err) {
        dispatch(setSendersFetchingError(getErrorMessage(err)));
        dispatch(setIsFetchingSenders(false));
        return false;
    }
};

export const fetchAllSenders = ({countOnly = false}: {countOnly?: boolean} = {}): SendersThunk<number | false> => async (dispatch, getState) => {
    const accountId = getAccountId(getState());

    dispatch(setSendersFetchingError(null));
    dispatch(setIsFetchingSenders(true));

    try {
        if (countOnly) {
            const seatConsumingCount = await getSeatConsumingAccountUsersCount(accountId);
            dispatch(setAllSendersCount(seatConsumingCount));
            dispatch(setIsFetchingSenders(false));
            return seatConsumingCount;
        }

        const response = await getPaginatedAccountUsers(accountId, {per_page: 100, scope: 'all'});
        const accountUsers = response.data;
        const seatConsumingCount = await getSeatConsumingAccountUsersCount(accountId);
        dispatch(setAllSendersCount(seatConsumingCount));

        const users = await getUsersForAccountUsers(accountUsers);
        dispatch(setAllSenders({accountUsers, users}));
        dispatch(setIsFetchingSenders(false));
        return seatConsumingCount;
    } catch {
        dispatch(setIsFetchingSenders(false));
        return false;
    }
};

export const inviteSender = (sender: SenderInviteInput | string): SendersThunk<boolean> => async (dispatch, getState) => {
    const accountId = getAccountId(getState());
    const email = typeof sender === 'string' ? sender : sender.email;
    dispatch(setInviteSenderError(null));

    try {
        let existingUser: User | null = null;
        try {
            const userResponse = await userApi.getUserByEmail({params: {email}});
            existingUser = userResponse.data;
        } catch {
            existingUser = null;
        }

        const profile = typeof sender === 'string' ? null : sender;
        const userId = existingUser
            ? existingUser.id
            : (await userApi.createUser({
                data: {
                    email,
                    first_name: profile?.firstName || '',
                    last_name: profile?.lastName || '',
                    organisation: profile?.company || undefined,
                    title: profile?.title || undefined
                }
            })).data.id;

        await accountUserApi.attachUserToAccount({accountId, userId});
        await dispatch(fetchSenders());
        return true;
    } catch (err) {
        dispatch(setInviteSenderError(getErrorMessage(err)));
        return false;
    }
};

export const updateSender = (sender: SenderProfileInput): SendersThunk<boolean> => async (dispatch, getState) => {
    const accountId = getAccountId(getState());
    const userId = sender.id;
    dispatch(setUpdatingSenderError(null));
    dispatch(setIsUpdatingSender(true));

    try {
        const hasProfileFields = sender.firstName !== undefined
            || sender.lastName !== undefined
            || sender.company !== undefined
            || sender.title !== undefined;
        if (hasProfileFields) {
            await userApi.updateUser({
                userId,
                data: {
                    first_name: sender.firstName,
                    last_name: sender.lastName,
                    organisation: sender.company,
                    title: sender.title
                }
            });
        }

        if (sender.status) {
            await accountUserApi.updateAccountUserStatus({
                accountId,
                userId,
                data: {status: sender.status}
            });
        }

        await dispatch(fetchSenders());
        dispatch(setIsUpdatingSender(false));
        return true;
    } catch (err) {
        dispatch(setIsUpdatingSender(false));
        dispatch(setUpdatingSenderError(getErrorMessage(err)));
        return false;
    }
};

export const deleteSender = (userId: string): SendersThunk<boolean> => async (dispatch, getState) => {
    const accountId = getAccountId(getState());
    dispatch(setDeletingSenderError(null));
    dispatch(setIsDeletingSender(true));

    try {
        await accountUserApi.detachUserFromAccount({accountId, userId});
        dispatch(setIsDeletingSender(false));
        return true;
    } catch (err) {
        dispatch(setIsDeletingSender(false));
        dispatch(setDeletingSenderError(getErrorMessage(err)));
        return false;
    }
};

const getComposedRows = (accountUsers: AccountUserResource[], users: Record<string, User>): SenderRow[] => (
    accountUsers
        .filter((accountUser) => Boolean(users[accountUser.user_id]))
        .map((accountUser) => ({accountUser, user: users[accountUser.user_id]}))
);

export const selectSenders = createSelector(
    (state: RootStateShape) => state.senders.accountUsers,
    (state: RootStateShape) => state.senders.users,
    (accountUsers, users): SenderRow[] => getComposedRows(accountUsers, users)
);

export const selectAllSenders = createSelector(
    (state: RootStateShape) => state.senders.allAccountUsers,
    (state: RootStateShape) => state.senders.users,
    (allAccountUsers, users): SenderRow[] => getComposedRows(allAccountUsers, users)
);

export const selectSendersFetchingError = (state: RootStateShape): ErrorMessage | null => state.senders.sendersFetchingError;
export const selectIsFetchingSenders = (state: RootStateShape): boolean => state.senders.isFetchingSenders;
export const selectSendersOptions = (state: RootStateShape): SendersOptions => state.senders.sendersOptions;
export const selectSendersCount = (state: RootStateShape): number | null => state.senders.sendersCount;
export const selectAllSendersCount = (state: RootStateShape): number | null => state.senders.allSendersCount;
export const selectInviteSenderError = (state: RootStateShape): ErrorMessage | null => state.senders.inviteSenderError;
export const selectUpdatingSenderError = (state: RootStateShape): ErrorMessage | null => state.senders.updatingSenderError;
export const selectIsUpdatingSender = (state: RootStateShape): boolean => state.senders.isUpdatingSender;
export const selectDeletingSenderError = (state: RootStateShape): ErrorMessage | null => state.senders.deletingSenderError;
export const selectIsDeletingSender = (state: RootStateShape): boolean => state.senders.isDeletingSender;
export const selectSendersPerPage = (state: RootStateShape): number => state.senders.sendersPerPage;

export default sendersSlice.reducer;
